import { normalizarTexto } from '../utils/textoBusqueda';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Tickets from './Tickets';
import { List as ListIcon, Calendar } from 'lucide-react';
import { Search, SlidersHorizontal, LayoutGrid, Ticket, FileText } from '../components/icons';

import { ticketsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import CreateTicketModal from '../components/CreateTicketModal';
import AttachmentsModal from '../components/AttachmentsModal';
import InvGateTicketCard from '../components/InvGateTicketCard';

// Pestañas de estado -- las mismas categorías que ya usaba esta página, solo
// con el estilo visual de la cinta de InvGate (usado en Tickets.jsx, la
// vista de staff). No incluye "Esperando por mí"/"Aprobaciones"/"Sin
// asignar": esos son conceptos de quien gestiona tickets de otros, no de
// quien solo ve los propios.
const TABS = [
  { id: 'todos', label: 'Todos', ring: 'ring-gray-400', dot: 'bg-gray-400 dark:bg-gray-600', activeBg: 'bg-gray-100 dark:bg-gray-750 border-gray-300 dark:border-gray-600' },
  { id: 'pendiente', label: 'Pendientes', ring: 'ring-yellow-400', dot: 'bg-yellow-500', activeBg: 'bg-yellow-50/90 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-800' },
  { id: 'asignado', label: 'Asignados', ring: 'ring-blue-400', dot: 'bg-blue-600', activeBg: 'bg-blue-50/90 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800' },
  { id: 'en proceso', label: 'En Proceso', ring: 'ring-amber-400', dot: 'bg-amber-600', activeBg: 'bg-amber-50/90 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800' },
  { id: 'resuelto', label: 'Resueltos', ring: 'ring-emerald-400', dot: 'bg-emerald-600', activeBg: 'bg-emerald-50/90 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800' },
  { id: 'cerrado', label: 'Cerrados', ring: 'ring-gray-400', dot: 'bg-gray-500', activeBg: 'bg-gray-100 dark:bg-gray-750 border-gray-300 dark:border-gray-600' },
];

function MisTicketsPropios() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority'); // 'priority' | 'date' | 'status'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicketForAttachments, setSelectedTicketForAttachments] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('misTicketsViewMode') || 'grid');

  useEffect(() => {
    localStorage.setItem('misTicketsViewMode', viewMode);
  }, [viewMode]);

  const fetchMisTickets = useCallback(async () => {
    try {
      const response = await ticketsAPI.getMisTickets();
      // El backend ahora devuelve { tickets: [...], pagination: {...} }
      const ticketsData = response.data.tickets || response.data;
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudieron cargar tus tickets', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchMisTickets();
  }, [fetchMisTickets]);

  // Conteos por estado -- alimentan la cinta de estadísticas y las pestañas
  const counts = useMemo(() => {
    const norm = (e) => (e || '').toLowerCase();
    return {
      todos: tickets.length,
      pendiente: tickets.filter(t => norm(t.estado) === 'pendiente').length,
      asignado: tickets.filter(t => norm(t.estado) === 'asignado').length,
      'en proceso': tickets.filter(t => norm(t.estado) === 'en proceso').length,
      resuelto: tickets.filter(t => norm(t.estado) === 'resuelto').length,
      cerrado: tickets.filter(t => norm(t.estado) === 'cerrado').length,
    };
  }, [tickets]);

  // Filtrado, búsqueda y orden -- mismo criterio que la vista de staff
  const filteredTickets = useMemo(() => {
    let result = Array.isArray(tickets) ? [...tickets] : [];

    if (filter !== 'todos') {
      result = result.filter(t => t.estado?.toLowerCase() === filter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => {
        const idMatch = String(t.id).includes(q);
        const descMatch = normalizarTexto(t.descripcion).includes(normalizarTexto(q));
        const catMatch = normalizarTexto(t.categoria).includes(normalizarTexto(q)) || normalizarTexto(t.subcategoria?.nombre).includes(normalizarTexto(q));
        return idMatch || descMatch || catMatch;
      });
    }

    const priorityWeight = { 'crítica': 4, critica: 4, alta: 3, media: 2, baja: 1 };
    result.sort((a, b) => {
      if (sortBy === 'priority') {
        const pA = priorityWeight[(a.prioridad || '').toLowerCase()] || 0;
        const pB = priorityWeight[(b.prioridad || '').toLowerCase()] || 0;
        return pB - pA;
      }
      if (sortBy === 'date') {
        return new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0);
      }
      return (a.estado || '').localeCompare(b.estado || '');
    });

    return result;
  }, [tickets, filter, searchQuery, sortBy]);

  // "Por vencer": tickets propios sin primera respuesta -- mismo criterio
  // honesto que usa Tickets.jsx (no hay una fecha de vencimiento SLA real
  // calculada todavía, así que no se inventa una cuenta regresiva).
  const dueSoonTickets = useMemo(() => {
    return tickets
      .filter(t => t.estado?.toLowerCase() === 'pendiente' && !(t.comentarios?.length > 0))
      .slice(0, 4);
  }, [tickets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-full pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mis Tickets</h1>
          <p className="text-gray-600 dark:text-gray-300">Seguimiento de tus solicitudes de soporte</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="group relative flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl overflow-hidden shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_6px_20px_rgba(37,99,235,0.23)] hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative flex items-center gap-2">
            <div className="p-1 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <span className="tracking-wide">Crear Nuevo Ticket</span>
          </div>
        </button>
      </div>

      {/* Cinta de estadísticas -- mismo estilo que la vista de staff */}
      <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 shadow-xs">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
                filter === tab.id ? `${tab.activeBg} shadow-xs ring-2 ${tab.ring}` : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
              }`}
            >
              <span className={`w-12 h-7 rounded-md ${tab.dot} text-white font-black text-sm flex items-center justify-center shadow-xs`}>
                {counts[tab.id] ?? 0}
              </span>
              <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar: buscador, orden y vista */}
      <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-800 p-2.5 flex flex-col md:flex-row items-center justify-between gap-3 shadow-xs">
        <div className="relative w-full md:flex-1">
          <div className="absolute left-3.5 inset-y-0 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar en mis tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300">
            <SlidersHorizontal className="w-3.5 h-3.5 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-medium"
            >
              <option value="priority">Prioridad</option>
              <option value="date">Fecha de Creación</option>
              <option value="status">Estado</option>
            </select>
          </div>

          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700 shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              title="Vista en Tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              title="Vista de Lista"
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Layout principal: tarjetas + panel lateral "Por vencer" */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        <div className="lg:col-span-8 xl:col-span-9 space-y-4">
          {filteredTickets.length === 0 ? (
            <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-750 p-12 text-center">
              <Ticket className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">
                {filter === 'todos' ? 'No tienes tickets' : `No hay tickets ${filter}`}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {filter === 'todos'
                  ? 'Crea tu primer ticket para solicitar soporte o reportar un incidente.'
                  : 'No hay solicitudes que coincidan con los filtros seleccionados.'}
              </p>
              <button
                onClick={() => { setFilter('todos'); setSearchQuery(''); }}
                className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 rounded-lg text-xs font-semibold hover:bg-blue-100"
              >
                Restablecer Filtros
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
              {filteredTickets.map((ticket) => (
                <InvGateTicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          )}
        </div>

        {/* Panel lateral: solo "Por vencer" -- las acciones de aprobar/completar
            tareas son de quien gestiona tickets de otros, no aplican aquí. */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-4 shadow-xs">
            <div className="flex items-center gap-2.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
              <span className="w-6 h-6 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center justify-center font-bold text-xs">
                {dueSoonTickets.length}
              </span>
              <div className="flex flex-col">
                <h4 className="text-xs font-bold text-gray-900 dark:text-white leading-tight">Por vencer</h4>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Sin primera respuesta</span>
              </div>
            </div>

            <div className="mt-3 space-y-2.5 text-xs">
              {dueSoonTickets.length === 0 ? (
                <div className="py-2 text-center text-gray-400 text-[11px]">
                  Todos tus tickets ya tienen una primera respuesta.
                </div>
              ) : (
                dueSoonTickets.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <Calendar className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-[11px] font-medium text-gray-800 dark:text-gray-200 truncate group-hover:text-blue-600">
                          {t.descripcion || 'Sin descripción'}
                        </span>
                        <span className="text-[10px] text-gray-400">#{t.id} • Pendiente</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {tickets.some(t => t.archivos?.length > 0) && (
            <div className="mt-4 bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-4 shadow-xs">
              <div className="flex items-center gap-2.5 pb-2 border-b border-gray-100 dark:border-gray-800">
                <FileText className="w-4 h-4 text-gray-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white">Con evidencia adjunta</h4>
              </div>
              <div className="mt-2 space-y-1.5">
                {tickets.filter(t => t.archivos?.length > 0).slice(0, 4).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTicketForAttachments(t)}
                    className="w-full text-left text-[11px] text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 truncate block"
                  >
                    #{t.id} · {t.archivos.length} archivo{t.archivos.length > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateTicketModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTicketCreated={fetchMisTickets}
      />

      {selectedTicketForAttachments && (
        <AttachmentsModal
          ticket={selectedTicketForAttachments}
          onClose={() => setSelectedTicketForAttachments(null)}
        />
      )}
    </div>
  );
}

// Admin y soporte alternan entre sus propios tickets y la bandeja de soporte; el resto solo ve los suyos.
export default function MisTickets() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const esStaff = user?.role === 'admin' || user?.role === 'soporte';
  const vista = esStaff && searchParams.get('vista') === 'soporte' ? 'soporte' : 'mios';

  if (!esStaff) return <MisTicketsPropios />;

  const cambiar = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'soporte') next.set('vista', 'soporte'); else next.delete('vista');
    setSearchParams(next, { replace: true });
  };
  const boton = (id, texto) => (
    <button
      type="button"
      onClick={() => cambiar(id)}
      aria-pressed={vista === id}
      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${vista === id ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
    >
      {texto}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
        {boton('mios', 'Mis tickets')}
        {boton('soporte', 'Vista de soporte')}
      </div>
      {vista === 'soporte' ? <Tickets /> : <MisTicketsPropios />}
    </div>
  );
}
